import React, { useState, useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import './index.scss'
import { Heading } from 'selfServicePortal/components'
import Modal from 'react-bootstrap/Modal'
import {
	FancyNumber,
	useFancyNumber,
	deriveFancyStep,
} from 'selfServicePortal/components/FancyNumber'
import { createWithdrawalService } from 'selfServicePortal/services/DepositServices/createWithdrawalService'
import { useQuery, useMutation } from '@tanstack/react-query'
import { getAssetsService } from 'selfServicePortal/services/DepositServices/getAssetsService'
import SspContext from 'selfServicePortal/contexts/SspContext'
import { BoxInfo, checkIsMobile } from 'shared'
import { SpinnerScreen } from 'selfServicePortal/components/ui/Spinner'
import { Input } from 'selfServicePortal/components/ui/Input'
import { Button } from 'react-bootstrap'

const assetType = {
	referenceAccount: 'PROD_9',
}

export default function WithdrawalDe() {
	const { t } = useTranslation()
	const [closeAccount, setCloseAccount] = useState(false)
	const [show, setShow] = useState(false)
	const { customerData, overviewData } = useContext(SspContext)
	const fancyNumber = useFancyNumber({
		minNumber: 0,
		maxNumber: Number(overviewData?.balance),
		step: deriveFancyStep(1, Number(overviewData?.balance)),
		initialNumber: 0,
	})
	const isMobile = checkIsMobile()

	const assetsQuery = useQuery({
		queryFn: () => getAssetsService(String(customerData?.customerId)),
		select: (assets) =>
			assets.filter(
				(asset) => asset.assetType === assetType.referenceAccount,
			),
	})
	const createWithdrawalMutation = useMutation({
		mutationFn: () =>
			createWithdrawalService({
				partyId: customerData?.customerId,
				firstName: customerData?.firstName,
				lastName: customerData?.lastName,
				nationalId: customerData?.nationalId,
				accountAgreementId: overviewData?.accountAgreementId,
				amount: fancyNumber.number,
				referenceAccountIban: choosenAsset?.iban,
				referenceAccountBic: choosenAsset?.bic,
				ocr: overviewData?.accountNumber,
				nameOnReferenceAccount: choosenAsset?.name,
				message: `Withdrawal was generated at ${new Date()}.`,
				closeAccount,
			}),
	})

	const [name, setName] = useState<string>()
	const choosenAsset = assetsQuery.data?.find(
		(assets) => assets.name === name,
	)

	useEffect(() => {
		setName(assetsQuery.data?.[0].name)
	}, [assetsQuery.data])

	function handleSubmit() {
		if (closeAccount) {
			setShow(true)
		} else {
			createWithdrawalMutation.mutate()
		}
	}

	if (createWithdrawalMutation.isLoading) {
		return <SpinnerScreen />
	}

	if (assetsQuery.isLoading) {
		return <SpinnerScreen />
	}

	if (createWithdrawalMutation.isSuccess) {
		return <BoxInfo css="success" message={t('WithdrawalSuccess')} />
	}

	if (createWithdrawalMutation.isError) {
		return (
			<BoxInfo
				css="fail"
				message={
					<span>
						Leider kann die Auszahlung aktuell nicht automatisiert
						ausgeführt werden. Bitte schicken Sie das{' '}
						<a href="https://tfbank.de/media/mj3mhf54/auszahlungsformular_tagesgeldkonto_tf-bank.pdf">
							Auszahlungsformular
						</a>{' '}
						ausgefüllt an die angegebene Adresse oder eingescannt
						per E-Mail an tagesgeld@tfbank.de zurück, oder versuchen
						Sie die automatisierte Auszahlung später erneut
						durchzuführen
					</span>
				}
			/>
		)
	}

	return (
		<div className="withdrawal-de">
			<div className="outer-container my-5">
				<Heading title={t('WithdrawalTitle')} />

				<div className="outer">
					<div className="info-container">
						<div className="info-head">
							{t('WithdrawalSubInfo')}
						</div>

						<div className="info">
							<span className="label">
								{t('WithdrawalHolder')}
							</span>
							<span className="value">
								<select
									className="form-select"
									value={name}
									onChange={({ target }) =>
										setName(target.value)
									}>
									{assetsQuery.data?.map((item) => (
										<option
											key={item.name}
											value={item.name}>
											{item.name}
										</option>
									))}
								</select>
							</span>
						</div>
						<div className="info">
							<span className="label">{t('WithdrawalBic')}</span>
							<span className="value">{choosenAsset?.bic}</span>
						</div>
						<div className="info">
							<span className="label">{t('WithdrawalIban')}</span>
							<span className="value">{choosenAsset?.iban}</span>
						</div>
						<div className="info">
							<span className="label">
								{isMobile
									? t('WithdrawalReference')
									: t('WithdrawalReferenceOcr')}
							</span>
							<span className="value">
								{overviewData?.accountNumber}
							</span>
						</div>
					</div>

					<FancyNumber>
						<FancyNumber.InputLabel>
							{t('WithdrawalInputLabel')}
						</FancyNumber.InputLabel>
						<Input.Container>
							<Input.Field
								value={fancyNumber.number}
								onChange={(e) =>
									fancyNumber.onNumberChange(
										Number(e.target.value),
									)
								}
								type="number"
								style={{ textAlign: 'center' }}
								disabled={closeAccount}
							/>
						</Input.Container>
						<FancyNumber.SliderLabel
							left={`€ ${fancyNumber.minNumber}`}
							right={`€ ${overviewData?.balance}`}
						/>
					</FancyNumber>

					<div>
						<label className="checkbox-container">
							<input
								type="checkbox"
								checked={closeAccount}
								onChange={() => {
									if (closeAccount === false) {
										fancyNumber.setNumber(
											fancyNumber.maxNumber,
										)
									}
									setCloseAccount((c) => !c)
								}}
							/>
							<span className="checkmark" />
							{t('WithdrawalCheckBoxLabel')}
						</label>
					</div>

					<div className="submit-button">
						<Button onClick={handleSubmit}>
							{t('WithdrawalSubmit')}
						</Button>
					</div>
				</div>
			</div>

			<Modal show={show} onHide={() => setShow(false)} centered>
				<Modal.Body>{t('WithdrawalConfirmationPrompt')}</Modal.Body>
				<Modal.Footer style={{ justifyContent: 'space-between' }}>
					<button
						type="button"
						className="btn btn-primary"
						onClick={() => createWithdrawalMutation.mutate()}>
						{t('WithdrawalContunie')}
					</button>
					<button
						type="button"
						className="btn btn-primary"
						onClick={() => setShow(false)}>
						{t('WithdrawalCancel')}
					</button>
				</Modal.Footer>
			</Modal>
		</div>
	)
}
